// src/components/Connectors/ConnectorsSection.jsx
// Full-page Connected Apps marketplace -- privacy-first BYO-token.
// NO OAuth, NO window.open sign-in, NO alert() connect flows.
// Only GitHub is backend-connectable today; all others show "Coming soon".

import { useState, useEffect, useCallback } from 'react';
import {
  Plug, Zap, Mail, BarChart3, FolderOpen, FileText, Video,
  Share2, MessageSquare, Globe, Palette, Smartphone,
  Database, Cloud, Code, CreditCard, Check, Download, Search
} from 'lucide-react';
import './connectors.css';

const API = 'https://vtaas-backend-220419304601.us-central1.run.app';

// ---- Sign-in URL map -------------------------------------------------------
// Privacy-first: clicking opens the provider's OWN login page in a new tab.
// We never broker authentication; users authenticate directly with the provider.
const SIGNIN_URLS = {
  // Google Suite
  gmail:        'https://mail.google.com',
  gcal:         'https://calendar.google.com',
  gdrive:       'https://drive.google.com',
  gsheets:      'https://docs.google.com/spreadsheets',
  youtube:      'https://www.youtube.com',
  // Social
  linkedin:     'https://www.linkedin.com',
  x:            'https://x.com',
  facebook:     'https://www.facebook.com',
  instagram:    'https://www.instagram.com',
  tiktok:       'https://www.tiktok.com',
  // Messaging
  slack:        'https://slack.com/signin',
  discord:      'https://discord.com/login',
  telegram:     'https://web.telegram.org',
  whatsapp:     'https://web.whatsapp.com',
  // Infrastructure
  firebase:     'https://console.firebase.google.com',
  cloudrun:     'https://console.cloud.google.com/run',
  supabase:     'https://supabase.com/dashboard',
  hostinger:    'https://hpanel.hostinger.com',
  // Productivity
  notion:       'https://www.notion.so/login',
  github:       'https://github.com/settings/personal-access-tokens',
  stripe:       'https://dashboard.stripe.com/login',
  zapier:       'https://zapier.com/app/login',
  // App Store integrations
  'firebase-i': 'https://console.firebase.google.com',
  'supabase-i': 'https://supabase.com/dashboard',
  'stripe-mcp': 'https://dashboard.stripe.com/login',
  paypal:       'https://www.paypal.com/signin',
  figma:        'https://www.figma.com/login',
  'github-mcp': 'https://github.com/login',
  linear:       'https://linear.app/login',
  'notion-mcp': 'https://www.notion.so/login',
  neon:         'https://console.neon.tech',
  mongodb:      'https://cloud.mongodb.com',
  redis:        'https://redis.io/try-free',
  bigquery:     'https://console.cloud.google.com/bigquery',
  'cloudrun-i': 'https://console.cloud.google.com/run',
  heroku:       'https://id.heroku.com/login',
  pinecone:     'https://app.pinecone.io',
  perplexity:   'https://www.perplexity.ai',
  atlassian:    'https://id.atlassian.com/login',
  prisma:       'https://console.prisma.io',
  airweave:     'https://app.airweave.ai',
  shopify:      'https://www.shopify.com/login',
  hubspot:      'https://app.hubspot.com/login',
  mailchimp:    'https://login.mailchimp.com',
  twilio:       'https://www.twilio.com/login',
  sendgrid:     'https://app.sendgrid.com/login',
};

// ---- Catalog ---------------------------------------------------------------

const groups = [
  {
    label: 'Google Suite',
    colorVar: 'var(--accent)',
    connectors: [
      { id: 'gmail',   name: 'Gmail',           desc: 'Read, send, manage emails',              Icon: Mail },
      { id: 'gcal',    name: 'Google Calendar',  desc: 'Create events, check schedule',          Icon: BarChart3 },
      { id: 'gdrive',  name: 'Google Drive',     desc: 'Upload, download, organize files',       Icon: FolderOpen },
      { id: 'gsheets', name: 'Google Sheets',    desc: 'Create and edit spreadsheets',           Icon: FileText },
      { id: 'youtube', name: 'YouTube',          desc: 'Upload, manage videos and analytics',    Icon: Video },
    ],
  },
  {
    label: 'Social Media',
    colorVar: 'var(--accent)',
    connectors: [
      { id: 'linkedin',  name: 'LinkedIn',     desc: 'Post, comment, message, network',     Icon: Share2 },
      { id: 'x',        name: 'X (Twitter)',   desc: 'Post tweets, engage, monitor',        Icon: MessageSquare },
      { id: 'facebook', name: 'Facebook',      desc: 'Post to pages, groups, stories',      Icon: Globe },
      { id: 'instagram',name: 'Instagram',     desc: 'Post images, reels, stories',         Icon: Palette },
      { id: 'tiktok',   name: 'TikTok',        desc: 'Create and publish short videos',     Icon: Video },
    ],
  },
  {
    label: 'Messaging',
    colorVar: 'oklch(0.72 0.17 160)',
    connectors: [
      { id: 'slack',    name: 'Slack',     desc: 'Send messages, manage channels',       Icon: MessageSquare },
      { id: 'discord',  name: 'Discord',   desc: 'Post in servers, manage bots',         Icon: MessageSquare },
      { id: 'telegram', name: 'Telegram',  desc: 'Send messages, manage bots',           Icon: MessageSquare },
      { id: 'whatsapp', name: 'WhatsApp',  desc: 'Business messaging, notifications',    Icon: Smartphone },
    ],
  },
  {
    label: 'Infrastructure',
    colorVar: 'oklch(0.65 0.20 25)',
    connectors: [
      { id: 'firebase', name: 'Firebase',         desc: 'Auth, Firestore, hosting, functions', Icon: Database },
      { id: 'cloudrun', name: 'Google Cloud Run', desc: 'Deploy containers, manage services',  Icon: Cloud },
      { id: 'supabase', name: 'Supabase',         desc: 'Postgres, auth, storage, realtime',   Icon: Database },
      { id: 'hostinger',name: 'Hostinger',        desc: 'Web hosting, domains, VPS',           Icon: Globe },
    ],
  },
  {
    label: 'Productivity',
    colorVar: 'oklch(0.78 0.16 70)',
    connectors: [
      { id: 'notion', name: 'Notion', desc: 'Create pages, databases, notes',   Icon: FileText },
      { id: 'github', name: 'GitHub', desc: 'Create repos, PRs, issues',        Icon: Code },
      { id: 'stripe', name: 'Stripe', desc: 'Payments, invoices, subscriptions',Icon: CreditCard },
      { id: 'zapier', name: 'Zapier', desc: 'Connect 5000+ apps',               Icon: Zap },
    ],
  },
];

const integrations = [
  { id: 'firebase-i',  name: 'Firebase',    desc: 'Database, auth, hosting, and cloud functions',   category: 'cloud' },
  { id: 'supabase-i',  name: 'Supabase',    desc: 'Open-source Firebase alternative with Postgres',  category: 'database' },
  { id: 'stripe-mcp',  name: 'Stripe',      desc: 'Payments, subscriptions, and invoicing APIs',     category: 'payments' },
  { id: 'paypal',      name: 'PayPal',       desc: 'Payment processing and merchant services',        category: 'payments' },
  { id: 'figma',       name: 'Figma',        desc: 'Design files, components, and dev handoff',       category: 'design' },
  { id: 'github-mcp',  name: 'GitHub',       desc: 'Repos, PRs, issues, and CI/CD pipelines',        category: 'dev' },
  { id: 'linear',      name: 'Linear',       desc: 'Issue tracking and project management',           category: 'dev' },
  { id: 'notion-mcp',  name: 'Notion',       desc: 'Workspaces, databases, and knowledge bases',     category: 'productivity' },
  { id: 'neon',        name: 'Neon',         desc: 'Serverless Postgres database',                    category: 'database' },
  { id: 'mongodb',     name: 'MongoDB',      desc: 'Document database via MongoDB Atlas',             category: 'database' },
  { id: 'redis',       name: 'Redis',        desc: 'In-memory key-value store',                      category: 'database' },
  { id: 'bigquery',    name: 'BigQuery',     desc: 'Google Cloud data warehouse and analytics',      category: 'cloud' },
  { id: 'cloudrun-i',  name: 'Cloud Run',    desc: 'Deploy and manage containerized apps',           category: 'cloud' },
  { id: 'heroku',      name: 'Heroku',       desc: 'PaaS for deploying and scaling apps',            category: 'cloud' },
  { id: 'pinecone',    name: 'Pinecone',     desc: 'Vector database for AI/ML embeddings',           category: 'ai' },
  { id: 'perplexity',  name: 'Perplexity',   desc: 'AI-powered search and research',                 category: 'ai' },
  { id: 'atlassian',   name: 'Atlassian',    desc: 'Jira, Confluence, and Bitbucket',                category: 'dev' },
  { id: 'prisma',      name: 'Prisma',       desc: 'ORM for TypeScript and Node.js',                 category: 'database' },
  { id: 'airweave',    name: 'Airweave',     desc: 'Universal app search for agents',                category: 'ai' },
  { id: 'shopify',     name: 'Shopify',      desc: 'E-commerce store management',                    category: 'commerce' },
  { id: 'hubspot',     name: 'HubSpot',      desc: 'CRM, marketing, and sales automation',           category: 'marketing' },
  { id: 'mailchimp',   name: 'Mailchimp',    desc: 'Email marketing and automation',                  category: 'marketing' },
  { id: 'twilio',      name: 'Twilio',       desc: 'SMS, voice, and communication APIs',             category: 'communication' },
  { id: 'sendgrid',    name: 'SendGrid',     desc: 'Transactional and marketing email',              category: 'communication' },
];

const categoryColor = {
  cloud: 'var(--accent)', database: 'var(--accent)', payments: 'oklch(0.72 0.17 160)',
  design: 'oklch(0.78 0.16 70)', dev: 'var(--accent)', productivity: 'oklch(0.78 0.16 70)',
  ai: 'var(--accent)', commerce: 'oklch(0.72 0.17 160)', marketing: 'oklch(0.82 0.18 55)',
  communication: 'var(--accent)',
};

// GitHub-specific token help
const TOKEN_HELP = {
  github: {
    url: 'https://github.com/settings/personal-access-tokens',
    hint: 'Generate a fine-grained token scoped to the repos you choose, with Issues set to Read and write. Set your own expiry.',
  },
};

// ---- Component -------------------------------------------------------------

export default function ConnectorsSection() {
  const [connTab, setConnTab] = useState('platforms');
  const [integrationSearch, setIntegrationSearch] = useState('');

  // Live state from backend: { [id]: { connected: bool, display_name: string } }
  const [liveById, setLiveById] = useState({});

  // Per-card token input / busy / error state
  const [tokenInputs, setTokenInputs] = useState({});
  const [showInput, setShowInput]   = useState({});
  const [busy, setBusy]     = useState({});
  const [errors, setErrors] = useState({});

  const hash = localStorage.getItem('qxai_user_hash');

  // Fetch backend connectors on mount
  const loadLive = useCallback(() => {
    if (!hash) return;
    fetch(`${API}/api/qxai/agent-native/connectors`, {
      headers: { 'X-User-Hash': hash },
    })
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data)) return;
        const map = {};
        data.forEach((c) => { map[c.id] = c; });
        setLiveById(map);
      })
      .catch(() => {});
  }, [hash]);

  useEffect(() => { loadLive(); }, [loadLive]);

  // Save token for a backend-live connector
  const handleSave = async (id) => {
    const token = (tokenInputs[id] || '').trim();
    if (!token) return;
    setBusy((b) => ({ ...b, [id]: true }));
    setErrors((e) => ({ ...e, [id]: null }));
    try {
      const res = await fetch(`${API}/api/qxai/agent-native/connectors/${id}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Hash': hash },
        body: JSON.stringify({ token }),
      }).then((r) => r.json());
      if (res.ok) {
        setTokenInputs((t) => ({ ...t, [id]: '' }));
        setShowInput((s) => ({ ...s, [id]: false }));
        loadLive();
      } else {
        setErrors((e) => ({ ...e, [id]: res.error || 'Could not save token.' }));
      }
    } catch {
      setErrors((e) => ({ ...e, [id]: 'Network error.' }));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  // Disconnect a backend-live connector
  const handleDisconnect = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await fetch(`${API}/api/qxai/agent-native/connectors/${id}`, {
        method: 'DELETE',
        headers: { 'X-User-Hash': hash },
      });
      loadLive();
    } catch {
      // silent
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const allConnectors = groups.flatMap((g) => g.connectors);
  const connectedCount = Object.values(liveById).filter((c) => c.connected).length;
  const availableCount = allConnectors.length - connectedCount;

  const filteredIntegrations = integrations.filter(
    (i) =>
      !integrationSearch ||
      i.name.toLowerCase().includes(integrationSearch.toLowerCase()) ||
      i.desc.toLowerCase().includes(integrationSearch.toLowerCase())
  );

  // ---- Render a single platform connector card ----------------------------
  const renderPlatformCard = (c, colorVar) => {
    const live = liveById[c.id];
    const isLive = !!live;            // backend knows about this connector
    const isConnected = live?.connected === true;
    const isBusy = !!busy[c.id];
    const errMsg = errors[c.id];
    const isShowingInput = showInput[c.id];
    const help = TOKEN_HELP[c.id] || {};

    return (
      <div key={c.id} className="cx-card">
        <c.Icon size={20} color={colorVar} className="cx-card-icon" />
        <div className="cx-card-body">
          <div className="cx-card-name">{c.name}</div>
          <div className="cx-card-desc">{c.desc}</div>

          {/* Inline token form for live connectors that are not yet connected */}
          {isLive && !isConnected && isShowingInput && (
            <div className="cx-token-form">
              {help.hint && <p className="cx-hint">{help.hint}</p>}
              {help.url && (
                <a
                  className="cx-hint-link"
                  href={help.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Generate a token on {c.name}
                </a>
              )}
              <div className="cx-token-row">
                <input
                  type="password"
                  className="cx-token-input"
                  placeholder="Paste your token"
                  value={tokenInputs[c.id] || ''}
                  onChange={(e) =>
                    setTokenInputs((t) => ({ ...t, [c.id]: e.target.value }))
                  }
                />
                <button
                  className="cx-btn cx-btn--primary"
                  disabled={isBusy}
                  onClick={() => handleSave(c.id)}
                >
                  {isBusy ? 'Saving...' : 'Save'}
                </button>
                <button
                  className="cx-btn"
                  onClick={() => setShowInput((s) => ({ ...s, [c.id]: false }))}
                >
                  Cancel
                </button>
              </div>
              {errMsg && <p className="cx-error">{errMsg}</p>}
            </div>
          )}
        </div>

        <div className="cx-card-actions">
          {isLive ? (
            isConnected ? (
              <>
                <span className="cx-dot cx-dot--ok" />
                <button
                  className="cx-status-btn"
                  disabled={isBusy}
                  onClick={() => handleDisconnect(c.id)}
                  title="Click to disconnect"
                >
                  {isBusy ? 'Disconnecting...' : 'Connected'}
                </button>
              </>
            ) : (
              <>
                <span className="cx-dot" />
                <button
                  className="cx-btn cx-btn--outline"
                  style={{ '--cx-outline-color': colorVar }}
                  onClick={() =>
                    setShowInput((s) => ({ ...s, [c.id]: !s[c.id] }))
                  }
                >
                  Connect
                </button>
              </>
            )
          ) : (
            SIGNIN_URLS[c.id]
              ? (
                <button
                  className="cx-btn cx-btn-signin"
                  onClick={() => window.open(SIGNIN_URLS[c.id], '_blank', 'noopener,noreferrer')}
                >
                  Sign In
                </button>
              )
              : <span className="cx-chip-soon">Coming soon</span>
          )}
        </div>
      </div>
    );
  };

  // ---- Render ---------------------------------------------------------------
  return (
    <div className="cx-page">
      <h1 className="cx-heading">Connected Apps</h1>
      <p className="cx-subhead">
        Connect your accounts with a token you generate and control. You set the scope
        and expiry; your token is encrypted and only you can use it.
      </p>

      {/* Tabs */}
      <div className="cx-tabs">
        <button
          className={`cx-tab ${connTab === 'platforms' ? 'is-active' : ''}`}
          onClick={() => setConnTab('platforms')}
        >
          <Plug size={14} />
          Platforms
        </button>
        <button
          className={`cx-tab ${connTab === 'integrations' ? 'is-active' : ''}`}
          onClick={() => setConnTab('integrations')}
        >
          <Zap size={14} />
          App Store
        </button>
      </div>

      {/* ---- PLATFORMS TAB ---- */}
      {connTab === 'platforms' && (
        <>
          <div className="cx-counts">
            <span className={`cx-badge ${connectedCount > 0 ? 'cx-badge--active' : 'cx-badge--dim'}`}>
              {connectedCount} Connected
            </span>
            <span className="cx-badge cx-badge--dim">{availableCount} Available</span>
          </div>

          {groups.map((group) => (
            <div key={group.label} className="cx-group">
              <h3 className="cx-group-label">
                <span className="cx-group-dot" style={{ background: group.colorVar }} />
                {group.label}
              </h3>
              <div className="cx-grid">
                {group.connectors.map((c) => renderPlatformCard(c, group.colorVar))}
              </div>
            </div>
          ))}
        </>
      )}

      {/* ---- APP STORE TAB ---- */}
      {connTab === 'integrations' && (
        <div>
          <div className="cx-search-bar">
            <Search size={16} className="cx-search-icon" />
            <input
              type="text"
              className="cx-search-input"
              placeholder="Search integrations..."
              value={integrationSearch}
              onChange={(e) => setIntegrationSearch(e.target.value)}
            />
          </div>

          <div className="cx-grid">
            {filteredIntegrations.map((i) => {
              const color = categoryColor[i.category] || 'var(--accent)';
              return (
                <div key={i.id} className="cx-card">
                  <div className="cx-int-icon" style={{ '--cx-int-color': color }}>
                    <Zap size={14} color={color} />
                  </div>
                  <div className="cx-card-body">
                    <div className="cx-card-name">{i.name}</div>
                    <div className="cx-card-desc">{i.desc}</div>
                  </div>
                  <div className="cx-card-actions">
                    {SIGNIN_URLS[i.id]
                      ? (
                        <button
                          className="cx-btn cx-btn-signin"
                          onClick={() => window.open(SIGNIN_URLS[i.id], '_blank', 'noopener,noreferrer')}
                        >
                          Sign In
                        </button>
                      )
                      : <span className="cx-chip-soon">Coming soon</span>
                    }
                  </div>
                </div>
              );
            })}
          </div>

          {filteredIntegrations.length === 0 && (
            <div className="cx-empty">No integrations match your search.</div>
          )}
        </div>
      )}
    </div>
  );
}
